/** The header a peek and the split pane share (1.I-2 deliverables 5 and 6): history, title and the two actions. */
import { docsMeta } from '@codex/contracts';
import { Button, IconButton } from '@codex/ui';
import { useQuery } from '@tanstack/react-query';
import { api } from '../auth/client.js';
import { metaKey } from '../shell/queries.js';
import { canBack, canForward, type PaneStack } from './pane-history.js';

/** A page's title through the shell's `docs.meta` cache; "Page" until it is known, or with no page. */
export function usePaneTitle(docId: string | null): string {
  const meta = useQuery({
    queryKey: metaKey(docId ?? ''),
    queryFn: () => api(docsMeta, { params: { id: docId as string } }),
    enabled: docId !== null,
    retry: false,
  });
  const title = meta.data?.title.trim();
  return title ? title : 'Page';
}

export interface PeekHeaderProps {
  readonly title: string;
  readonly stack: PaneStack;
  /** Back and Forward are shown for the side peek and the split pane, not the center peek. */
  readonly history: boolean;
  readonly closeLabel: string;
  readonly onBack: () => void;
  readonly onForward: () => void;
  readonly onOpenAsPage: () => void;
  /** "Open in split", on the side peek only. */
  readonly onOpenInSplit?: (() => void) | undefined;
  readonly onClose: () => void;
}

export function PeekHeader(props: PeekHeaderProps) {
  return (
    <div className="cx-peek__header cx-nav-peek__header">
      {props.history && (
        <div className="cx-nav-peek__history">
          <IconButton
            icon="arrow-left"
            label="Back"
            disabled={!canBack(props.stack)}
            onClick={props.onBack}
          />
          <IconButton
            icon="arrow-right"
            label="Forward"
            disabled={!canForward(props.stack)}
            onClick={props.onForward}
          />
        </div>
      )}
      <h2 className="cx-nav-peek__title">{props.title}</h2>
      <div className="cx-peek__actions">
        {props.onOpenInSplit && (
          <Button size="sm" variant="ghost" onClick={props.onOpenInSplit}>
            Open in split
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={props.onOpenAsPage}>
          Open as page
        </Button>
        <IconButton icon="x" label={props.closeLabel} onClick={props.onClose} />
      </div>
    </div>
  );
}
